/**
 * Parts of a downloaded model that exist in more than one state.
 *
 * WHY THIS EXISTS
 * The Tomcat was running up the starboard side at 350 knots with its
 * undercarriage hanging out, and the reason was not that the gear was
 * unmodelled — it was that it was modelled TWICE and both copies were being
 * drawn. The author of this glb built `f14a-gearOn` (the legs, the wheels and
 * the open doors, ten thousand triangles of it) and `f14a-gearOff` (a hundred
 * and eighty-seven triangles of closed bay doors), which is exactly what you
 * would do, and nothing was reading the node names to find out.
 *
 * Worth the reminder: before writing geometry, read what the model already has.
 *
 * The convention is `<something>-<part>On` / `<part>Off`, so this is generic
 * rather than a hard-coded list of one aeroplane's node names. A model that
 * does not use it yields nothing and nothing changes.
 *
 * Pure string handling, kept out of ModelOverrides so it can be tested without
 * a glTF loader or a GPU.
 */

/** `f14a-gearOn_4` -> `{ part: 'gear', state: 'on' }`, or null. */
export function parseVariantName(name) {
  const m = /^(?:.*?-)?([A-Za-z][A-Za-z0-9]*?)(On|Off)(?:_\d+)?$/.exec(name ?? '')
  if (!m) return null
  return { part: m[1].toLowerCase(), state: m[2].toLowerCase() }
}

/**
 * Group named things by part and state.
 *
 * @param {Array<{name?:string}>} nodes
 * @returns {Record<string, {on?:object, off?:object}>}
 */
export function collectVariants(nodes) {
  const out = {}
  for (const node of nodes) {
    const v = parseVariantName(node?.name)
    if (!v) continue
    out[part(v)] ??= {}
    out[part(v)][v.state] = node
  }
  return out

  function part(v) {
    return v.part
  }
}

/**
 * Which parts have BOTH halves, and are therefore switchable.
 *
 * A one-sided match is deliberately harmless rather than an error: a model with
 * a node called `leftPylon` parses as the part "leftPyl" in state "on", and
 * since nothing will ever produce a matching "off" it is never touched. That is
 * the right failure mode for a heuristic reading arbitrary names — it can be
 * wrong without being destructive.
 */
export function switchableParts(variants) {
  return Object.entries(variants)
    .filter(([, v]) => v.on && v.off)
    .map(([k]) => k)
    .sort()
}
